/* eslint-disable no-return-assign */
import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactVimeo from '@u-wave/react-vimeo'
import HubspotForm from 'react-hubspot-form'
import { Modal } from 'react-responsive-modal'
import { BASQUIAT_CONTENTFUL_ID } from '../../utils/vars'
import ContentfulClient from '../../contentful'
import Layout from '../../components/layout'
import MetaHeaders from '../../components/metaheaders'
import PaintingDetailed from '../../components/paintingdetailed'
import ny1 from '../../assets/ny-1.jpg'
import ny2 from '../../assets/ny-2.jpg'
import ny3 from '../../assets/ny-3.jpg'
import txtPic from '../../assets/text-pic.jpg'
import basquiatMainPainting from '../../assets/basquiat-painting.png'
import secondBasquiatPainting from '../../assets/second-basquiat.png'
import PicTxt from '../../components/pictxt'
import ContactSection from '../../components/contactsection'
import PaintingSection from '../../components/paintingsection'
import close from '../../assets/detailed-close.svg'
import styles from './Gallery.module.scss'
import og from '../../assets/og-painting-image.jpg'

// paintings
import viking1 from '../../assets/basquiat-paintings/1/1.jpg'
import viking2 from '../../assets/basquiat-paintings/1/2.jpg'
import viking3 from '../../assets/basquiat-paintings/1/3.jpg'

import tupac1 from '../../assets/basquiat-paintings/2/1.jpg'
import tupac2 from '../../assets/basquiat-paintings/2/2.jpg'
import tupac3 from '../../assets/basquiat-paintings/2/3.png'

import green1 from '../../assets/basquiat-paintings/3/1.jpg'
import green2 from '../../assets/basquiat-paintings/3/2.jpg'
import green3 from '../../assets/basquiat-paintings/3/3.jpg'

import methodMan1 from '../../assets/basquiat-paintings/4/1.jpg'
import methodMan2 from '../../assets/basquiat-paintings/4/2.jpg'
import methodMan3 from '../../assets/basquiat-paintings/4/3.png'

import snoopDawg1 from '../../assets/basquiat-paintings/5/1.jpg'
import snoopDawg2 from '../../assets/basquiat-paintings/5/2.jpg'
import snoopDawg3 from '../../assets/basquiat-paintings/5/3.png'

import burningMan1 from '../../assets/basquiat-paintings/6/1.jpg'
import burningMan2 from '../../assets/basquiat-paintings/6/2.jpg'
import burningMan3 from '../../assets/basquiat-paintings/6/3.jpg'

import blueCapGuy1 from '../../assets/basquiat-paintings/7/1.jpg'
import blueCapGuy2 from '../../assets/basquiat-paintings/7/2.jpg'
import blueCapGuy3 from '../../assets/basquiat-paintings/7/3.png'

import beardGuy1 from '../../assets/basquiat-paintings/8/1.jpg'
import beardGuy2 from '../../assets/basquiat-paintings/8/2.jpg'
import beardGuy3 from '../../assets/basquiat-paintings/8/3.png'

import bwRapper1 from '../../assets/basquiat-paintings/9/1.jpg'
import bwRapper2 from '../../assets/basquiat-paintings/9/2.jpg'
import bwRapper3 from '../../assets/basquiat-paintings/9/3.png'

import willyWonka1 from '../../assets/basquiat-paintings/10/1.jpg'
import willyWonka2 from '../../assets/basquiat-paintings/10/2.jpg'
import willyWonka3 from '../../assets/basquiat-paintings/10/3.png'

const Gallery = ({
    metaTitle, metaDescription, videoUrl, title, subTitle,
}) => {
    const [showContactForm, setShowContactForm] = useState(false)
    const [videoLoaded, setVideoLoaded] = useState(false)

    const videoContainerRef = useRef(null)
    const appearOnScroll = useRef(null)

    const onCloseModal = () => {
        setShowContactForm(false)
    }

    useEffect(() => {
        const { offsetTop, offsetHeight } = videoContainerRef.current
        setVideoLoaded(window.scrollY > (offsetTop + offsetHeight))
    }, [])

    useEffect(() => {
        const faders = document.querySelectorAll('.slide-up')

        if (typeof window.IntersectionObserver === 'function') {
            appearOnScroll.current = new IntersectionObserver((entries, currObserver) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('appear')
                        currObserver.unobserve(entry.target)
                    }
                })
            }, {
                threshold: 0.25,
            })
            faders.forEach((fader) => {
                appearOnScroll.current.observe(fader)
            })
        } else {
            faders.forEach((fader) => {
                fader.classList.add('appear')
            })
        }
    }, [])

    return (
        <div className={styles.galleryContainer}>
            <MetaHeaders title={metaTitle} description={metaDescription} customOgImage={og} />
            {showContactForm && (
                <Modal
                    open={showContactForm}
                    onClose={() => onCloseModal()}
                    classNames={{
                        overlay: 'hubSpotOverlay',
                        modal: 'hubSpotModal',
                    }}
                >
                    <HubspotForm
                        portalId="7162797"
                        formId="c54ada93-fb89-4748-8f26-8634772bc10a"
                    />
                    <button
                        type="button"
                        className="closeBtn"
                        onClick={() => setShowContactForm(false)}
                    >
                        <img alt="Close contact form" src={close} />
                        <div className="sr-only">Close contact form</div>
                    </button>
                </Modal>
            )}
            <Layout>
                <div className={`${styles.gallery} ${styles.container}`}>
                    <h1 className={styles.seo}>{title}</h1>
                    <h2 className={styles.seo}>{subTitle}</h2>
                    <div className={styles.videoContainer} ref={videoContainerRef} style={{ height: videoLoaded ? 'auto' : 4000 }}>
                        <ReactVimeo
                            video="https://vimeo.com/444757856/149e65627d"
                            onLoaded={() => setVideoLoaded(true)}
                            muted
                            loop
                            autoplay
                            responsive
                        />
                    </div>
                    <div className={`row ${styles.slideUp} slide-up`}>
                        <div className={`${styles.galleryTxt} colThreeQuartersLg`}>
                            <p className={styles.firstTxt}>
                                <span className={styles.heading}>
                                    THE VIRTUAL EXHIBITION OF
                                    <br />
                                    STiCH INSPIRED BY JEAN-MICHEL BASQUIAT
                                </span>
                                {' '}
                                <br />
                                <br />
                                Up until only a few short years ago, art was once considered the
                                exclusive domain of human creation. Although, with
                                artificial intelligence now able to
                                synthesize its complex characteristics
                                with those from other disciplines, it turns
                                out machines can do more in the
                                way of creative reasoning than was once believed.
                            </p>
                        </div>
                    </div>
                    <div className={`${styles.slideUp} slide-up`}>
                        <ReactVimeo video={videoUrl} responsive />
                    </div>
                    <div className={`${styles.slideUp} slide-up`}>
                        <PicTxt pic={txtPic} caption="From the very beginning, musicians, painters, designers, architects and writers once scoffed at the idea of adversarial automation, however, with machine learning now capable of aggrandizing everything from poetry, music, food, and movie scripts, a good portion of those voices have since drowned in the surrounding noise. Those who remain steadfast with their own humanistic processes feel machines fail to understand the sentiment needed to create original art and is something that should be reliant on “mortal” compasses exclusively." />
                    </div>
                    <div className={`${styles.slideUp} slide-up row`}>
                        <PaintingDetailed
                            paintings={[snoopDawg3, snoopDawg2, snoopDawg1]}
                            title="STiCH"
                            credit="Untitled (b88Z0a5f-d7cb)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                        <PaintingDetailed
                            paintings={[methodMan3, methodMan2, methodMan1]}
                            title="STiCH"
                            credit="Untitled (27f3a102-5b74)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                    </div>
                    <div className={`${styles.slideUp} slide-up row`}>
                        <div className={`${styles.galleryTxt} colThreeQuartersLg`}>
                            <p>
                                The truth of the matter is, when it comes to creativity,
                                humans are being challenged by the very objects they’ve
                                created.
                            </p>
                            <p>Is that not a sign of true progress?</p>
                        </div>
                    </div>
                    <div className={`row ${styles.nyArtGallery}`}>
                        <div className={styles.galleryCol}>
                            <img alt="An abstract painting at an art gallery" src={ny1} className={`${styles.slideUp} slide-up`} />
                        </div>
                        <div className={styles.galleryCol}>
                            <img alt="A high rise view between two buildings" src={ny2} className={`${styles.slideUp} slide-up`} />
                        </div>
                        <div className={styles.galleryCol}>
                            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                            <img alt="A woman taking a picture of an abstract painting at an art gallery with her back facing the original shot" src={ny3} className={`${styles.slideUp} slide-up`} />
                        </div>
                    </div>
                    <p className={`${styles.slideUp} ${styles.nyCaption} slide-up`}>New york x basquiat</p>
                    <div className={`${styles.slideUp} slide-up`}>
                        <PaintingSection painting={basquiatMainPainting} />
                    </div>
                    <div className="row">
                        <PaintingDetailed
                            paintings={[burningMan3, burningMan2, burningMan1]}
                            title="STiCH"
                            credit="Untitled (c92fa065-913e)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                        <PaintingDetailed
                            paintings={[tupac3, tupac2, tupac1]}
                            title="STiCH"
                            credit="Untitled (2d2c71fd-024a)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                        <PaintingDetailed
                            paintings={[viking3, viking2, viking1]}
                            title="STiCH"
                            credit="Untitled (1bc639f5-fcc0)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                        <PaintingDetailed
                            paintings={[green3, green2, green1]}
                            title="STiCH"
                            credit="Untitled (6e22f42e-cbd6)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                    </div>
                    <div className={`${styles.slideUp} slide-up row`}>
                        <div className={`${styles.galleryTxt} colThreeQuartersLg`}>
                            <p>
                                After nearly three years of development, we are proud to
                                introduce the world’s first commercially-deployed AI artist
                                named STiCH. Adeptly trained in both art and design (equivalent
                                to a BFA level degree), STiCH is gearing up to create a new set
                                of prints through the poetic prism of late Neo-Expressionist
                                painter, Jean-Michel Basquiat.
                            </p>
                            <p>
                                The revolutionary machine has been tasked with creating renders
                                of influential artists from the world’s of music and entertainment
                                as if designed by the one and only painter. To prepare, STiCH will
                                comb through countless creations from the artist’s vast archive,
                                taking weeks to familiarize itself with his expressive oeuvre.
                            </p>
                            <p>
                                The end result will be a collection of modern day creatives
                                who were illustrated by Basquiat as if he were still alive today.
                                Taken from the world much too soon, his work still resonates today
                                and is often the target of cross-dimensional projects outside of
                                visual art, namely music and fashion.
                                How would the late, great artist view today’s most prevalent
                                pop-culture creators? STiCH seems to have the answer.
                            </p>
                        </div>
                    </div>
                    <div className={`${styles.slideUp} slide-up`}>
                        <PaintingSection painting={secondBasquiatPainting} />
                    </div>
                    <div className="row">
                        <PaintingDetailed
                            paintings={[willyWonka3, willyWonka2, willyWonka1]}
                            title="STiCH"
                            credit="Untitled (af8569f6-1ef3)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                        <PaintingDetailed
                            paintings={[bwRapper3, bwRapper2, bwRapper1]}
                            title="STiCH"
                            credit="Untitled (9a1efa69-8dae)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                        <PaintingDetailed
                            paintings={[blueCapGuy3, blueCapGuy2, blueCapGuy1]}
                            title="STiCH"
                            credit="Untitled (6e22f42e-cbd6)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                        <PaintingDetailed
                            paintings={[beardGuy3, beardGuy2, beardGuy1]}
                            title="STiCH"
                            credit="Untitled (8c0b24ed-2499)"
                            desc="Digital synthesis with visual style harmonization algorithm on liquid crystal display"
                            showContactForm={() => setShowContactForm(true)}
                        />
                    </div>
                    <div className={`${styles.slideUp} slide-up row`}>
                        <div className={`${styles.galleryTxt} colThreeQuartersLg`}>
                            <p>
                                The UCL Gallery is a virtual exhibition showcasing
                                art collections using creative personalities portrayed and
                                recreated using the artificial intelligence, STiCH. It uses
                                harmonisation aglorithms to not just only recreate the style
                                of the artist, but to follow the artist’s abstract; their
                                expression of their reality, their thematic choices, and their
                                feelings about their subjects.
                            </p>
                            <p className={styles.inquireSection}>Inquire with us if you:</p>
                            <dl className={styles.inquireSection}>
                                <dt>- Are interested in commissioning artwork with STiCH.</dt>
                                <dt>
                                    - Wish to be kept in the loop on upcoming exhibitions,
                                    latest artwork releases, or technology developments.
                                </dt>
                                <dt>- Are an artist interested in collaborating with STiCH.</dt>
                                <dt>- Have a suggestion on who STiCH should study next.</dt>
                            </dl>
                        </div>
                    </div>
                    <div className={`${styles.slideUp} slide-up`}>
                        <ContactSection showContactForm={() => setShowContactForm(true)} />
                        <p className={styles.disclaimer}>
                            The Jean-Michel Basquiat C/O STiCH collection was created as a homage
                            to the late American artist Jean-Michel Basquiat by urbancoolab’s
                            artificial intelligence STiCH. urbancoolab, and this collection is in
                            no way affiliated, associated, endorsed by, or connected to the Estate
                            of Jean-Michel Basquiat.
                        </p>
                    </div>
                </div>
            </Layout>
        </div>
    )
}

Gallery.propTypes = {
    metaTitle: PropTypes.string.isRequired,
    metaDescription: PropTypes.string.isRequired,
    videoUrl: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.string.isRequired,
}

Gallery.getInitialProps = async () => {
    const res = await ContentfulClient.getEntry(BASQUIAT_CONTENTFUL_ID)
    return res.fields
}

export default Gallery
